import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, MapPin, ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import api from "../../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const ProjectDetailsPage = () => {
  const pageRef = useRef(null);
  const { slug } = useParams();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProject = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/single/${slug}`);
      setProject(res.data);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [slug]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
        ease: "power3.out",
      });

      gsap.from(".project-detail-card", {
        opacity: 0,
        y: 60,
        duration: 1,
        stagger: 0.12,
        ease: "power3.out",
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, [project]);

  if (loading) {
    return <p className="py-32 text-center text-slate-500">Loading project...</p>;
  }

  if (!project) {
    return (
      <p className="py-32 text-center text-slate-500">
        Project not found.
      </p>
    );
  }

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[600px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Project Details
              </span>
            </div>

            <h1
              className="hero-title text-4xl md:text-6xl lg:text-[72px] font-black text-white leading-[1.02] max-w-5xl mx-auto"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              {project.title}
            </h1>

            {project.summary && (
              <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
                {project.summary}
              </p>
            )}

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>

              <ChevronRight size={14} />

              <Link
                to="/projects"
                className="hover:text-[#ff6600] transition-colors"
              >
                Projects
              </Link>

              <ChevronRight size={14} />

              <span className="text-[#ff6600]">{project.title}</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 mb-8 text-[#06183f] hover:text-[#ff6600] font-black"
          >
            <ArrowLeft size={18} />
            Back to Projects
          </Link>

          <div className="grid lg:grid-cols-[1fr_360px] gap-8">
            <article className="project-detail-card bg-white rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.10)] overflow-hidden">
              {project.featured_image && (
                <img
                  src={project.featured_image}
                  alt={project.title}
                  className="w-full h-[360px] md:h-[460px] object-cover"
                />
              )}

              <div className="p-6 md:p-9">
                <div
                  className="prose prose-lg max-w-none prose-headings:text-[#06183f] prose-a:text-[#ff6600]"
                  dangerouslySetInnerHTML={{ __html: project.content }}
                />
              </div>
            </article>

            <aside className="space-y-6">
              <div className="project-detail-card bg-white rounded-[30px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.10)] p-6">
                <h3
                  className="text-2xl font-black text-[#06183f] mb-5"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Project Information
                </h3>

                {project.category && (
                  <div className="mb-5">
                    <p className="text-sm font-bold text-slate-500 mb-2">
                      Status
                    </p>

                    <span
                      className="inline-flex px-5 py-3 rounded-full text-white text-xs font-black uppercase"
                      style={{
                        backgroundColor: project.category.color || "#ff6600",
                      }}
                    >
                      {project.category.name}
                    </span>
                  </div>
                )}

                {project.location && (
                  <div className="mb-5">
                    <p className="text-sm font-bold text-slate-500 mb-2">
                      Location
                    </p>

                    <p className="flex items-center gap-2 text-[#06183f] font-bold">
                      <MapPin size={17} />
                      {project.location}
                    </p>
                  </div>
                )}

                {project.features?.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-slate-500 mb-3">
                      Project Features
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.features.map((item) => (
                        <span
                          key={item}
                          className="px-4 py-2 rounded-xl bg-[#06183f]/5 text-[#06183f] text-xs font-bold"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProjectDetailsPage;